import { jsx as _jsx } from "react/jsx-runtime";
/**
 * PwaProvider —— 所有 PWA 单例在这里创建一次。
 *
 * 为什么要有 Provider：命名空间、Service Worker 注册、权限管理器、未读控制器这些东西
 * **一个应用只能有一份**。散在各个组件里各创建各的，会出现"两个未读控制器互相覆盖角标"
 * 这种查都查不出来的问题。
 *
 * ⚠️ 注册 Service Worker 的时机：不要等 React 挂载完再注册（首屏会慢），也不要在模块顶层
 * 同步注册（拿不到配置）。这里在 effect 里注册，但 `beforeinstallprompt` 的抓取是在
 * `@pwaship/core/install` 模块加载时就完成的——那个事件太早了，等 effect 就晚了。
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createNamespace, createSafeStorage, migrateLegacyKeys } from '@pwaship/core/storage';
import { createInstallState } from '@pwaship/core/standalone';
import { createNotificationPermission, createGestureTracker } from '@pwaship/core/permission';
import { createUnreadController } from '@pwaship/core/unread';
import { createTelemetry, consoleSink, noopSink } from '@pwaship/core/telemetry';
import { detectCapabilities } from '@pwaship/core/capability';
import { currentPlatform } from '@pwaship/core/platform';
import { registerServiceWorker } from '@pwaship/core/update';
import { consumePendingNav, onSwNavigate } from '@pwaship/core/nav';
import { createNeutralHistory } from '@pwaship/core/nav-history';
const PwaContext = createContext(null);
export function usePwa() {
    const ctx = useContext(PwaContext);
    if (!ctx)
        throw new Error('usePwa() 必须在 <PwaProvider> 里面用');
    return ctx;
}
export function PwaProvider({ config, onNavigate, children }) {
    const [update, setUpdate] = useState(null);
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [registration, setRegistration] = useState(null);
    const navRef = useRef(onNavigate);
    navRef.current = onNavigate;
    // 单例只建一次。注意 storage 迁移必须**在任何模块读键之前**跑完，
    // 所以放在 useMemo 的最前面——顺序错了，语言/主题/应用锁会先读到空值再把默认值写回去。
    const base = useMemo(() => {
        const ns = createNamespace(config.namespace);
        if (config.legacyNamespace)
            migrateLegacyKeys(ns.ns, config.legacyNamespace);
        const storage = createSafeStorage();
        // 默认往 console 打，**生产也打**：PWA 的坑绝大多数只在真机上出现，而真机排障唯一
        // 的手段就是 Safari/Chrome 远程调试看这些日志。一行一条、带 [pwaship] 前缀，可以 grep。
        // 不想要就传 telemetrySinks: 'none'。
        const telemetry = createTelemetry(config.telemetrySinks === 'none' ? [noopSink] : (config.telemetrySinks ?? [consoleSink]));
        const gesture = createGestureTracker();
        const install = createInstallState(ns, storage);
        install.rememberIfStandalone();
        const permission = createNotificationPermission(ns, { storage, gesture });
        const unread = createUnreadController({
            getRegistration: async () => {
                if (typeof navigator === 'undefined' || !('serviceWorker' in navigator))
                    return undefined;
                return (await navigator.serviceWorker.getRegistration()) ?? undefined;
            },
            onError: (stage, error) => telemetry.emit({ type: 'sw.error', stage: `unread.${stage}`, message: String(error) }),
        });
        // 中性化路由要在**任何路由代码读位置之前**建好：它的构造函数会把首帧的真实 URL
        // 解析出来再抹掉地址栏，晚一步就意味着深链已经被别人按 window.location 读过了（那时是 '/'）。
        const history = config.neutralHistory === true
            ? createNeutralHistory({ onError: (stage, e) => telemetry.emit({ type: 'sw.error', stage: `history.${stage}`, message: String(e) }) })
            : config.neutralHistory
                ? createNeutralHistory({
                    onError: (stage, e) => telemetry.emit({ type: 'sw.error', stage: `history.${stage}`, message: String(e) }),
                    ...config.neutralHistory,
                })
                : null;
        return { ns, storage, telemetry, install, permission, unread, history };
        // ⚠️ neutralHistory 是**启动期决策**，故意不进依赖数组：中途开关它意味着地址栏语义
        // 在运行中改变，那不是一个能安全支持的状态。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config.namespace, config.legacyNamespace]);
    // 未读三态同步的监听：回到前台就按当前未读重扫通知中心
    useEffect(() => base.unread.start(), [base]);
    // 中性化路由的 popstate 监听要跟着 Provider 卸载一起摘掉
    useEffect(() => () => base.history?.destroy(), [base]);
    // Service Worker 注册
    useEffect(() => {
        if (config.disableServiceWorker)
            return;
        let cancelled = false;
        let created = null;
        void registerServiceWorker({
            url: config.swUrl ?? '/sw.js',
            ...(config.swType ? { type: config.swType } : {}),
            strategy: config.updateStrategy ?? 'prompt',
            onRegistered: (reg) => {
                if (!cancelled)
                    setRegistration(reg);
                base.telemetry.emit({ type: 'sw.registered' });
            },
            onUpdateAvailable: () => {
                if (!cancelled)
                    setUpdateAvailable(true);
                base.telemetry.emit({ type: 'sw.update-available' });
            },
            onError: (error) => base.telemetry.emit({ type: 'sw.error', stage: 'register', message: String(error) }),
        }).then((result) => {
            if (cancelled || !result.ok)
                return;
            created = result.value;
            setUpdate(result.value);
        });
        return () => {
            cancelled = true;
            // ⚠️ 一定要拆。这个 effect 在 config 变化时会重跑，StrictMode 下还会跑两遍，
            // 而 controller 里挂着一个每小时 registration.update() 的 setInterval
            // 和一个 visibilitychange 监听。不拆的话一个几天不关的 PWA 会攒出一堆重复更新检查，
            // 失败时还会重复触发 onError——表现是"日志里同一条错误刷屏"，很难联想到这里。
            created?.dispose();
        };
    }, [base, config.disableServiceWorker, config.swUrl, config.swType, config.updateStrategy]);
    // 深链：冷启动从 Cache 里取（iOS 上 openWindow 会丢路径），已有窗口走 postMessage
    useEffect(() => {
        const cacheName = base.ns.cache('nav');
        const maxAgeMs = config.pendingNavMaxAgeMs ?? 60_000;
        // 中性化开着的时候，深链**只能**走内部路由：navRef 那条路会让调用方拿 path 去
        // pushState 一个真实 URL，等于把刚抹掉的东西又写回地址栏。
        const route = (path) => {
            base.telemetry.emit({ type: 'push.click', target: path });
            if (base.history)
                base.history.navigate(path);
            else
                navRef.current?.(path);
        };
        void consumePendingNav(cacheName, { maxAgeMs }).then((path) => {
            if (path)
                route(path);
        });
        return onSwNavigate(route);
    }, [base, config.pendingNavMaxAgeMs]);
    const value = useMemo(() => ({
        ...base,
        platform: currentPlatform(),
        capabilities: detectCapabilities(),
        update,
        updateAvailable,
        registration,
        config,
    }), [base, update, updateAvailable, registration, config]);
    return _jsx(PwaContext.Provider, { value: value, children: children });
}
//# sourceMappingURL=provider.js.map